"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Milestone = { period: string; title: string; body: string; icon?: ReactNode; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson" };

const ACCENT_TEXT: Record<NonNullable<Milestone["accent"]>, string> = {
  red: "text-red-400", coral: "text-coral-400", purple: "text-purple-400", cyan: "text-cyan-400", denim: "text-denim-400", emerald: "text-emerald-400", lime: "text-lime-400", "amber-gold": "text-amber-gold-400", crimson: "text-crimson-400",
};
const ACCENT_BG: Record<NonNullable<Milestone["accent"]>, string> = {
  red: "from-red-500/20 to-red-500/0", coral: "from-coral-500/20 to-coral-500/0", purple: "from-purple-500/20 to-purple-500/0", cyan: "from-cyan-500/20 to-cyan-500/0", denim: "from-denim-500/20 to-denim-500/0", emerald: "from-emerald-500/20 to-emerald-500/0", lime: "from-lime-500/20 to-lime-500/0", "amber-gold": "from-amber-gold-500/20 to-amber-gold-500/0", crimson: "from-crimson-500/20 to-crimson-500/0",
};
const ACCENT_DOT: Record<NonNullable<Milestone["accent"]>, string> = {
  red: "bg-red-500", coral: "bg-coral-500", purple: "bg-purple-500", cyan: "bg-cyan-500", denim: "bg-denim-500", emerald: "bg-emerald-500", lime: "bg-lime-500", "amber-gold": "bg-amber-gold-500", crimson: "bg-crimson-500",
};

export function TimelineBlock({ milestones, title, eyebrow, description, orientation = "horizontal" }: { milestones: Milestone[]; title?: string; eyebrow?: string; description?: string; orientation?: "horizontal" | "vertical" }) {
  return (
    <div className="space-y-10">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      {orientation === "horizontal" ? (
        <div className="relative">
          <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-obsidian-700 via-platinum-500/30 to-obsidian-700 lg:block" />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-6">
            {milestones.map((m, i) => {
              const accent = m.accent ?? "purple";
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="relative">
                  <div className="mb-4 flex items-center gap-3">
                    <div className={cn("h-3.5 w-3.5 rounded-full ring-4 ring-background", ACCENT_DOT[accent])} />
                    {i < milestones.length - 1 && <div className="hidden h-px flex-1 bg-muted lg:block" />}
                  </div>
                  <div className="glass-card relative overflow-hidden rounded-2xl border p-5" style={{ borderColor: "rgb(63, 63, 70)" }}>
                    <div className={cn("absolute inset-0 bg-gradient-to-br", ACCENT_BG[accent])} />
                    <div className="relative">
                      <div className={cn("text-[10px] font-bold uppercase tracking-[0.2em]", ACCENT_TEXT[accent])}>{m.period}</div>
                      <h3 className="mt-2 font-rajdhani text-base font-bold text-foreground">{m.title}</h3>
                      <p className="mt-2 text-xs leading-relaxed text-foreground/80/85">{m.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="relative mx-auto max-w-3xl">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-obsidian-700 via-platinum-500/30 to-obsidian-700" />
          <div className="space-y-8">
            {milestones.map((m, i) => {
              const accent = m.accent ?? "purple";
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="relative grid grid-cols-[auto_1fr] gap-6 pl-2">
                  <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-card ring-2 ring-border">
                    <div className={cn("h-3 w-3 rounded-full", ACCENT_DOT[accent])} />
                  </div>
                  <div className="glass-card relative overflow-hidden rounded-2xl border border-border p-5">
                    <div className={cn("absolute inset-0 bg-gradient-to-br", ACCENT_BG[accent])} />
                    <div className="relative">
                      <div className={cn("text-[10px] font-bold uppercase tracking-[0.2em]", ACCENT_TEXT[accent])}>{m.period}</div>
                      <h3 className="mt-1 font-rajdhani text-lg font-bold text-foreground">{m.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground/80/85">{m.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
